// ProductionShowcasePage — Demonstrates the complete production component library
// Toast, Sheet, Skeleton, Icon, Brand marks, currency formatting, haptics

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from '../../components/Toast';
import { Sheet } from '../../components/Sheet';
import { Skeleton, SkeletonText, SkeletonCard, SkeletonList } from '../../components/Skeleton';
import { Icon } from '../../components/Icon';
import { haptic } from '../../components/Haptics';
import {
  VisaMark, MastercardMark, AmexMark, ApplePayMark, GooglePayMark, PayPalMark, WiseMark,
  BitcoinMark, EthereumMark, UsdcMark,
  StripeMark, PersonaMark, TwilioMark, TradingViewMark, TwelveDataMark, CloudflareMark, RithmicMark,
  DiscordMark, XMark, InstagramMark, YouTubeMark, TelegramMark, TikTokMark,
  formatCurrency, formatNumber, formatDate, formatTime, CURRENCY_SYMBOLS,
} from '../../components/BrandAssets';

export function ProductionShowcasePage() {
  const nav = useNavigate();
  const [currency, setCurrency] = useState<keyof typeof CURRENCY_SYMBOLS>('USD');
  const [showSheet, setShowSheet] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [showFullscreen, setShowFullscreen] = useState(false);
  const [showSkeleton, setShowSkeleton] = useState(false);

  return (
    <div className="min-h-screen bg-tf-surface-1 pb-8">
      <div className="sticky top-0 z-10 flex h-11 items-center justify-between border-b border-tf-border ios26-navbar px-2 backdrop-blur-xl">
        <button onClick={() => nav(-1)} data-testid="showcase-back" className="flex h-9 w-9 items-center justify-center text-tf-blue haptic">
          <Icon name="chevron-left" size={20} />
        </button>
        <div className="absolute left-1/2 -translate-x-1/2 text-[15px] font-semibold">Production Suite</div>
        <div className="w-9" />
      </div>

      {/* Hero */}
      <div className="mx-4 mt-4">
        <div className="ios26-hero text-center" style={{ background: 'linear-gradient(135deg, #1C1C1E 0%, #007AFF 100%)', color: 'white' }} data-testid="showcase-hero">
          <div className="text-[11px] uppercase tracking-wider" style={{ color: 'rgba(255,255,255,0.85)' }}>Native iOS · Component Library</div>
          <h1 className="mt-1 text-[26px] font-bold tracking-tight">Production Suite</h1>
          <p className="text-[12px] mt-1" style={{ color: 'rgba(255,255,255,0.85)' }}>Every component, brand mark, and helper — production-ready</p>
        </div>
      </div>

      {/* Toast */}
      <Section title="Toast / Snackbar" subtitle="Auto-dismissing notifications">
        <div className="grid grid-cols-2 gap-2">
          <button data-testid="toast-success" onClick={() => { haptic('success'); toast.success('Payout sent', '$1,240 sent to Chase Bank'); }} className="rounded-[10px] border border-tf-border bg-white p-3 text-center">
            <Icon name="checkmark.circle" size={22} color="#34C759" />
            <div className="text-[12px] font-semibold mt-1">Success</div>
          </button>
          <button data-testid="toast-error" onClick={() => { haptic('error'); toast.error('Connection lost', 'Retrying in 5s…'); }} className="rounded-[10px] border border-tf-border bg-white p-3 text-center">
            <Icon name="xmark.circle" size={22} color="#FF3B30" />
            <div className="text-[12px] font-semibold mt-1">Error</div>
          </button>
          <button data-testid="toast-info" onClick={() => { haptic('light'); toast.info('Bot paused', 'News blackout in 30s'); }} className="rounded-[10px] border border-tf-border bg-white p-3 text-center">
            <Icon name="info.circle" size={22} color="#007AFF" />
            <div className="text-[12px] font-semibold mt-1">Info</div>
          </button>
          <button data-testid="toast-warning" onClick={() => { haptic('warning'); toast.warning('Daily loss 80%', 'Approaching 2% limit'); }} className="rounded-[10px] border border-tf-border bg-white p-3 text-center">
            <Icon name="exclamationmark.triangle" size={22} color="#FF9500" />
            <div className="text-[12px] font-semibold mt-1">Warning</div>
          </button>
        </div>
      </Section>

      {/* Sheet/Dialog */}
      <Section title="Sheet & Dialog" subtitle="Bottom sheet, dialog, fullscreen">
        <div className="grid grid-cols-3 gap-2">
          <button data-testid="open-sheet" onClick={() => setShowSheet(true)} className="rounded-[10px] border border-tf-border bg-white p-3 text-center">
            <Icon name="rectangle.stack" size={22} color="#007AFF" />
            <div className="text-[11px] font-semibold mt-1">Sheet</div>
          </button>
          <button data-testid="open-dialog" onClick={() => setShowDialog(true)} className="rounded-[10px] border border-tf-border bg-white p-3 text-center">
            <Icon name="square.grid" size={22} color="#5856D6" />
            <div className="text-[11px] font-semibold mt-1">Dialog</div>
          </button>
          <button data-testid="open-fullscreen" onClick={() => setShowFullscreen(true)} className="rounded-[10px] border border-tf-border bg-white p-3 text-center">
            <Icon name="arrow.up.left.and.arrow.down.right" size={22} color="#FF9500" />
            <div className="text-[11px] font-semibold mt-1">Full</div>
          </button>
        </div>
      </Section>

      {/* Skeleton */}
      <Section title="Skeleton loaders" subtitle="Shimmer animation">
        <button data-testid="toggle-skeleton" onClick={() => setShowSkeleton(!showSkeleton)} className="mb-2 text-[12px] text-tf-blue active:opacity-40">
          {showSkeleton ? 'Show loaded state' : 'Show skeleton state'}
        </button>
        {showSkeleton ? (
          <div className="space-y-3">
            <SkeletonCard />
            <SkeletonList rows={3} />
            <SkeletonText lines={4} />
          </div>
        ) : (
          <div className="space-y-3">
            <div className="rounded-[16px] bg-white p-4">
              <div className="text-[14px] font-bold text-tf-text-1">Bot is running</div>
              <p className="text-[12px] text-tf-text-3 mt-1">NQ momentum · +$842 / 7d</p>
              <div className="mt-2 text-[11px] text-tf-text-3">12 trades today · 67% win rate</div>
            </div>
            <div className="rounded-[12px] bg-white">
              {['Open positions', 'Trade history', 'Bot config'].map((t) => (
                <div key={t} className="flex items-center gap-3 p-3 border-b border-tf-border last:border-b-0">
                  <div className="h-9 w-9 rounded-[10px] bg-tf-blue/15 flex items-center justify-center"><Icon name="chart" size={18} color="#007AFF" /></div>
                  <div className="flex-1">
                    <div className="text-[13px] font-semibold text-tf-text-1">{t}</div>
                    <div className="text-[11px] text-tf-text-3">All time</div>
                  </div>
                  <Icon name="chevron-right" size={16} color="#C7C7CC" />
                </div>
              ))}
            </div>
          </div>
        )}
      </Section>

      {/* Icons */}
      <Section title="SF Symbol-style icons" subtitle="120+ icons, semantic colors">
        <div className="grid grid-cols-8 gap-1.5" data-testid="icon-grid">
          {(['home', 'chart', 'wallet', 'person', 'gear', 'search', 'bell', 'plus', 'check', 'xmark', 'chevron-right', 'arrow-up', 'arrow-down', 'star', 'heart', 'lock', 'eye', 'doc', 'envelope', 'phone', 'briefcase', 'globe', 'cart', 'gift', 'shield', 'flag', 'bolt', 'cube', 'creditcard', 'banknote', 'dollarsign', 'gauge', 'target', 'clock', 'calendar', 'map', 'play', 'pause', 'mic', 'trophy', 'crown', 'flame', 'leaf', 'drop', 'sparkles', 'paintbrush', 'scissors']).map((n) => (
            <div key={n} className="aspect-square rounded-[8px] bg-white border border-tf-border flex items-center justify-center active:scale-90 transition" data-testid={`icon-${n}`}>
              <Icon name={n as any} size={20} color="#007AFF" />
            </div>
          ))}
        </div>
      </Section>

      {/* Brand assets — payment */}
      <Section title="Payment brand marks" subtitle="Official brand SVGs (not placeholders)">
        <div className="grid grid-cols-3 gap-2.5" data-testid="payment-grid">
          <BrandCard><VisaMark size={56} /></BrandCard>
          <BrandCard><MastercardMark size={56} /></BrandCard>
          <BrandCard><AmexMark size={56} /></BrandCard>
          <BrandCard><ApplePayMark size={56} /></BrandCard>
          <BrandCard><GooglePayMark size={56} /></BrandCard>
          <BrandCard><PayPalMark size={56} /></BrandCard>
          <BrandCard><WiseMark size={56} /></BrandCard>
          <BrandCard><BitcoinMark size={32} /></BrandCard>
          <BrandCard><EthereumMark size={32} /></BrandCard>
          <BrandCard><UsdcMark size={32} /></BrandCard>
        </div>
      </Section>

      {/* Brand assets — partners */}
      <Section title="Partner brand marks" subtitle="Integrated service providers">
        <div className="grid grid-cols-4 gap-2.5" data-testid="partner-grid">
          <BrandCard label="Stripe"     sub="Payments"><StripeMark size={32} /></BrandCard>
          <BrandCard label="Persona"    sub="KYC"><PersonaMark size={32} /></BrandCard>
          <BrandCard label="Twilio"     sub="SMS"><TwilioMark size={32} /></BrandCard>
          <BrandCard label="TradingView" sub="Charts"><TradingViewMark size={32} /></BrandCard>
          <BrandCard label="TwelveData" sub="Prices"><TwelveDataMark size={32} /></BrandCard>
          <BrandCard label="Cloudflare" sub="Hosting"><CloudflareMark size={32} /></BrandCard>
          <BrandCard label="Rithmic"    sub="Broker"><RithmicMark size={32} /></BrandCard>
          <BrandCard label="Discord"    sub="Community"><DiscordMark size={32} /></BrandCard>
        </div>
      </Section>

      {/* Brand assets — social */}
      <Section title="Social brand marks" subtitle="Community channels">
        <div className="grid grid-cols-3 gap-2.5" data-testid="social-grid">
          <BrandCard label="X"          sub="Twitter"><XMark size={32} /></BrandCard>
          <BrandCard label="Instagram"  sub="Social"><InstagramMark size={32} /></BrandCard>
          <BrandCard label="YouTube"    sub="Videos"><YouTubeMark size={32} /></BrandCard>
          <BrandCard label="Telegram"   sub="Chat"><TelegramMark size={32} /></BrandCard>
          <BrandCard label="TikTok"     sub="Short"><TikTokMark size={32} /></BrandCard>
          <BrandCard label="Discord"    sub="Server"><DiscordMark size={32} /></BrandCard>
        </div>
      </Section>

      {/* Currency & Locale */}
      <Section title="Currency & localization" subtitle="20 currencies, locale-aware formatting">
        <div className="mb-2 flex flex-wrap gap-1.5">
          {Object.keys(CURRENCY_SYMBOLS).slice(0, 10).map((c) => (
            <button key={c} onClick={() => setCurrency(c as any)} data-testid={`currency-${c}`}
              className={['rounded-full px-2.5 py-1 text-[11px] font-bold transition',
                currency === c ? 'bg-tf-blue text-white' : 'bg-white border border-tf-border text-tf-text-1'
              ].join(' ')}
            >{CURRENCY_SYMBOLS[c as keyof typeof CURRENCY_SYMBOLS].flag} {c}</button>
          ))}
        </div>
        <div className="rounded-[12px] bg-white p-3 space-y-2" data-testid="currency-display">
          <div className="flex justify-between"><span className="text-[11px] text-tf-text-3">Balance</span><span className="text-[14px] font-bold text-tf-text-1 tabular-nums">{formatCurrency(12480.50, currency)}</span></div>
          <div className="flex justify-between"><span className="text-[11px] text-tf-text-3">P&L</span><span className="text-[14px] font-bold text-tf-profit tabular-nums">{formatCurrency(842.30, currency)}</span></div>
          <div className="flex justify-between"><span className="text-[11px] text-tf-text-3">Fee</span><span className="text-[14px] font-bold text-tf-loss tabular-nums">−{formatCurrency(2.99, currency)}</span></div>
          <div className="flex justify-between"><span className="text-[11px] text-tf-text-3">Today</span><span className="text-[14px] text-tf-text-1">{formatDate(new Date(), currency)}</span></div>
          <div className="flex justify-between"><span className="text-[11px] text-tf-text-3">Time</span><span className="text-[14px] text-tf-text-1">{formatDate(new Date('2026-07-24T15:42:00Z'), currency)} {formatTime(new Date('2026-07-24T15:42:00Z'), currency)}</span></div>
          <div className="flex justify-between"><span className="text-[11px] text-tf-text-3">Volume</span><span className="text-[14px] text-tf-text-1 tabular-nums">{formatNumber(1842000, currency)}</span></div>
        </div>
      </Section>

      {/* Sheets */}
      <Sheet open={showSheet} onClose={() => setShowSheet(false)} variant="sheet" title="Bottom sheet" testId="demo-sheet">
        <p className="text-[13px] text-tf-text-2">Bottom sheets slide up from the bottom edge with a drag handle. They use spring physics for natural motion.</p>
        <button onClick={() => setShowSheet(false)} className="mt-3 w-full h-11 rounded-full bg-tf-blue text-white text-[14px] font-semibold active:opacity-40">Dismiss</button>
      </Sheet>

      <Sheet open={showDialog} onClose={() => setShowDialog(false)} variant="dialog" title="Centered dialog" testId="demo-dialog">
        <p className="text-[13px] text-tf-text-2">Dialogs are centered modals with a backdrop. Use for confirmations and important decisions.</p>
        <div className="mt-3 flex gap-2">
          <button onClick={() => setShowDialog(false)} className="flex-1 h-11 rounded-full bg-tf-surface-1 text-[14px] font-semibold text-tf-text-1 active:opacity-40">Cancel</button>
          <button onClick={() => setShowDialog(false)} className="flex-1 h-11 btn-spring">Confirm</button>
        </div>
      </Sheet>

      <Sheet open={showFullscreen} onClose={() => setShowFullscreen(false)} variant="fullscreen" title="Fullscreen" testId="demo-fullscreen">
        <div className="p-4">
          <h2 className="text-[20px] font-bold mb-2">Fullscreen sheet</h2>
          <p className="text-[13px] text-tf-text-2">Fullscreen sheets take over the entire viewport with a custom header. Great for modal workflows like KYC or settings.</p>
        </div>
      </Sheet>
    </div>
  );
}

function Section({ title, subtitle, children }: { title: string; subtitle: string; children: React.ReactNode }) {
  return (
    <div className="mx-4 mt-5" data-testid={`section-${title.toLowerCase().split(' ')[0]}`}>
      <h2 className="text-[11px] font-semibold uppercase tracking-wider text-tf-text-3">{title}</h2>
      <p className="text-[10px] text-tf-text-3 mb-2">{subtitle}</p>
      {children}
    </div>
  );
}

function BrandCard({ children, label, sub }: { children: React.ReactNode; label?: string; sub?: string }) {
  return (
    <div className="rounded-[10px] border border-tf-border bg-white p-2.5 flex flex-col items-center justify-center min-h-[80px]">
      <div className="mb-1">{children}</div>
      {label && <div className="text-[10px] font-bold text-tf-text-1">{label}</div>}
      {sub && <div className="text-[9px] text-tf-text-3">{sub}</div>}
    </div>
  );
}
